"use client";

import { useState, useEffect, useCallback } from "react";
import { AppShell, ActionIcon, Box } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { IconChevronLeft, IconChevronRight } from "@tabler/icons-react";
import classes from "./ChatLayoutMantine.module.css";

type ChatLayoutMantineProps = {
  sidebar: React.ReactNode;
  children: React.ReactNode;
  /** Which sidebar type for collapse state persistence */
  sidebarType?: "chat" | "dashboard" | "deep-dives" | "fb-insights" | "dashboard-page" | "data-management" | "activation" | "insights" | "automations";
};

export default function ChatLayoutMantine({
  sidebar,
  children,
  sidebarType = "chat",
}: ChatLayoutMantineProps) {
  // Detect small screens (below 768px)
  const isSmallScreen = useMediaQuery("(max-width: 768px)");

  // Storage keys based on sidebar type
  const storageKey = `${sidebarType}-sidebar-collapsed`;
  const eventName = `${sidebarType}-sidebar-toggle`;

  // Track sidebar collapsed state from localStorage
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const stored = localStorage.getItem(storageKey);
    // Default to collapsed for dashboard sidebar, expanded for others
    if (stored === null && sidebarType === "dashboard") return true;
    return stored === "true";
  });

  // Auto-collapse on small screens
  useEffect(() => {
    if (isSmallScreen && !sidebarCollapsed) {
      // Auto-collapse when screen becomes small
      localStorage.setItem(storageKey, "true");
      setSidebarCollapsed(true);
      window.dispatchEvent(new CustomEvent(eventName));
    }
  }, [isSmallScreen, storageKey, eventName]);

  useEffect(() => {
    const handleStorage = (e: StorageEvent) => {
      if (e.key === storageKey) {
        setSidebarCollapsed(e.newValue === "true");
      }
    };

    // Also listen for custom events from same window
    const handleCustomEvent = () => {
      setSidebarCollapsed(localStorage.getItem(storageKey) === "true");
    };

    window.addEventListener("storage", handleStorage);
    window.addEventListener(eventName, handleCustomEvent);

    return () => {
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener(eventName, handleCustomEvent);
    };
  }, [storageKey, eventName]);

  const handleToggleCollapse = useCallback(() => {
    const next = localStorage.getItem(storageKey) !== "true";
    localStorage.setItem(storageKey, String(next));
    setSidebarCollapsed(next);
    window.dispatchEvent(new CustomEvent(eventName));
  }, [storageKey, eventName]);

  return (
    <AppShell
      header={{ height: 64 }}
      padding={0}
      navbar={{
        width: sidebarCollapsed ? 68 : 260,
        // No breakpoint - sidebar never hides, only collapses
        breakpoint: 0,
        collapsed: { desktop: false, mobile: false },
      }}
      styles={{
        // Header is rendered by AuthenticatedShell; we only keep
        // header={{ height: 64 }} so the fixed navbar gets top: 64px.
        // Override the main padding-top that Mantine adds for the header.
        main: {
          paddingTop: "0px",
        },
      }}
    >
      {/* No AppShell.Header — rendered by AuthenticatedShell */}

      {/* SIDEBAR with hover-reveal collapse toggle on the edge */}
      <AppShell.Navbar
        p={0}
        className={classes.navbar}
        style={{
          display: "flex",
          flexDirection: "column",
          background: sidebarType === "chat" ? "#ffffff" : "linear-gradient(180deg, #fdfdfe 0%, #fbfbfc 40%, #fdfcfb 100%)",
          boxShadow: "2px 0 6px rgba(0, 0, 0, 0.04)",
        }}
      >
        {sidebar}

        {/* Hover-reveal collapse/expand toggle on the right edge */}
        <Box
          onClick={handleToggleCollapse}
          style={{
            position: "absolute",
            top: "50%",
            right: -12,
            transform: "translateY(-50%)",
            zIndex: 10,
            cursor: "pointer",
            opacity: 0,
            transition: "opacity 0.2s ease",
          }}
          className={classes.edgeToggle}
        >
          <ActionIcon
            variant="filled"
            color="gray.1"
            size={24}
            radius="xl"
            style={{
              border: "1px solid var(--mantine-color-gray-3)",
              color: "var(--mantine-color-gray-6)",
            }}
          >
            {sidebarCollapsed ? <IconChevronRight size={14} /> : <IconChevronLeft size={14} />}
          </ActionIcon>
        </Box>
      </AppShell.Navbar>

      {/* MAIN CONTENT - styling handled by dark-theme.css */}
      <AppShell.Main
        style={{
          display: "flex",
          flexDirection: "column",
          height: "calc(100vh - 64px)",
          minHeight: 0,
          background: "linear-gradient(180deg, #fdfdfe 0%, #fbfbfc 40%, #fdfcfb 100%)",
        }}
      >
        {children}
      </AppShell.Main>
    </AppShell>
  );
}
